import { useEffect, useState } from "react";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Checkbox from "../../components/common/Checkbox";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import Section from "../../components/common/Section";
import { useStore } from "../../context/StoreContext";
import {
  useGetBusinessGeneralSettingsQuery,
  useGetStoreFeaturesQuery,
  useUpdateBusinessGeneralSettingsMutation,
} from "../../store/api";

const GeneralSettings = () => {
  const { selectedStoreId } = useStore();
  const {
    data: settings,
    isLoading,
    isError,
    error,
  } = useGetBusinessGeneralSettingsQuery();
  const { data: storeFeatures } = useGetStoreFeaturesQuery(selectedStoreId, {
    skip: !selectedStoreId,
  });
  const [updateSettings, updateState] =
    useUpdateBusinessGeneralSettingsMutation();

  const [form, setForm] = useState({
    requireCustomerDetails: false,
    showAllergens: false,
    showDietTags: false,
  });
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    if (settings) {
      setForm({
        requireCustomerDetails: Boolean(settings.requireCustomerDetails),
        showAllergens: Boolean(settings.showAllergens),
        showDietTags: Boolean(settings.showDietTags),
      });
    }
  }, [settings]);

  const showKioskVisibility = storeFeatures
    ? Boolean(storeFeatures?.enableKiosk)
    : true;

  const handleChange = (event) => {
    const { name, checked } = event.target;
    setForm((prev) => ({ ...prev, [name]: checked }));
    setSuccessMessage("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSuccessMessage("");
    try {
      await updateSettings({
        requireCustomerDetails: Boolean(form.requireCustomerDetails),
        showAllergens: Boolean(form.showAllergens),
        showDietTags: Boolean(form.showDietTags),
      }).unwrap();
      setSuccessMessage("General settings updated.");
    } catch (err) {
      // error handled by UI
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="General Settings"
        subtitle="Configure business-wide preferences for ordering behavior."
      />

      <Card>
        <form className="space-y-6" onSubmit={handleSubmit}>
          <Section
            title="Customer Details"
            subtitle="Apply to all stores under this business."
          >
            <div className="grid gap-4 md:grid-cols-2">
              <Checkbox
                label="Require customer details"
                name="requireCustomerDetails"
                checked={form.requireCustomerDetails}
                onChange={handleChange}
              />
            </div>
          </Section>

          {showKioskVisibility && (
            <Section
              title="Kiosk Visibility"
              subtitle="Control which details appear on kiosk ordering."
            >
              <div className="grid gap-4 md:grid-cols-2">
                <Checkbox
                  label="Show Allergens on Kiosk"
                  name="showAllergens"
                  checked={form.showAllergens}
                  onChange={handleChange}
                />
                <Checkbox
                  label="Show Diet Tags on Kiosk"
                  name="showDietTags"
                  checked={form.showDietTags}
                  onChange={handleChange}
                />
              </div>
            </Section>
          )}


          {isLoading && (
            <Alert tone="info">Loading current settings...</Alert>
          )}
          {isError && (
            <Alert tone="error">
              {error?.data?.detail || "Failed to load general settings."}
            </Alert>
          )}
          {successMessage && <Alert tone="success">{successMessage}</Alert>}
          {updateState.isError && (
            <Alert tone="error">
              {updateState.error?.data?.detail ||
                "Failed to update general settings."}
            </Alert>
          )}

          <div className="flex gap-3">
            <Button type="submit" disabled={updateState.isLoading}>
              {updateState.isLoading ? "Saving..." : "Save Settings"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default GeneralSettings;
